"use client";

import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useFormContext } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { MilkdownProvider } from "@milkdown/react";

import { Button } from "@/components/shadcn/button";
import { Switch } from "@/components/shadcn/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { ComplexMarkdownEditor } from "@/components/Form/ComplexMarkdownField/ComplexMarkdownField";
import { BindableInput } from "./BindableInput";
import {
  NoxusFormLabel,
  PinFieldContext,
} from "@/components/Form/NoxusFormLabel";
import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { cn } from "@/lib/utils";
import { useUpstreamVariableOptions } from "./InlineVariablePicker";
import { nodeOptionsToEditorInputs } from "./editorInputsFromOptions";
import { WHOLE_VAR_REF_PATTERN } from "./variableRefs";

// One row of the bindable condition list. The `field` and `value` slots (and
// the AI prompt) store bindable strings (literal text or `:var[<ref>]`); the
// V2 runtime deep-walks the config and resolves refs before the node runs.
export interface ConditionRow {
  logic?: "OR" | "AND";
  condition_type?: "REGULAR" | "AI";
  regular_condition?: {
    field_type?: "TEXT" | "NUMBER" | "BOOLEAN";
    field?: string;
    condition?: string;
    value?: string;
  };
  ai_condition?: {
    prompt?: string;
  };
}

const FIELD_TYPES = [
  { value: "TEXT", label: "Text" },
  { value: "NUMBER", label: "Number" },
  { value: "BOOLEAN", label: "Boolean" },
] as const;

const TEXT_OPS = [
  { value: "CONTAINS", label: "Contains" },
  { value: "DOES_NOT_CONTAIN", label: "Doesn't contain" },
  { value: "STARTS_WITH", label: "Starts with" },
  { value: "EQUALS", label: "Equals" },
  { value: "NOT_EQUALS", label: "Doesn't equal" },
  { value: "IS_EMPTY", label: "Is empty" },
  { value: "IS_NOT_EMPTY", label: "Is not empty" },
];

// Operators that test the field alone — the Value input is hidden for these.
const VALUELESS_OPS = new Set(["IS_EMPTY", "IS_NOT_EMPTY"]);

const NUMBER_OPS = [
  { value: "EQUALS", label: "=" },
  { value: "NOT_EQUALS", label: "≠" },
  { value: "GREATER_THAN", label: ">" },
  { value: "GREATER_THAN_OR_EQUAL", label: "≥" },
  { value: "SMALLER_THAN", label: "<" },
  { value: "SMALLER_THAN_OR_EQUAL", label: "≤" },
];

const BOOLEAN_OPS = [
  { value: "EQUALS", label: "Is" },
  { value: "NOT_EQUALS", label: "Is not" },
];

const opsFor = (fieldType: string | undefined) =>
  fieldType === "NUMBER"
    ? NUMBER_OPS
    : fieldType === "BOOLEAN"
      ? BOOLEAN_OPS
      : TEXT_OPS;

function dataTypeFor(fieldType: string | undefined): string {
  if (fieldType === "NUMBER") return "number";
  if (fieldType === "BOOLEAN") return "bool";
  return "str";
}

type ConditionBlurKey =
  | `${number}:field`
  | `${number}:value`
  | `${number}:prompt`;

function isEmptyBindableValue(value: string | undefined): boolean {
  return !value?.trim();
}

function isVariableRef(value: string | undefined): boolean {
  return typeof value === "string" && WHOLE_VAR_REF_PATTERN.test(value);
}

function FieldWarning({ show, message }: { show: boolean; message: string }) {
  if (!show) return null;
  return (
    <NoxusAlert look="banner" title={message} variant="warning" size="xs" />
  );
}

function AIPromptField({
  prompt,
  disabled,
  invalid,
  onChange,
  onBlur,
}: {
  prompt: string;
  disabled?: boolean;
  invalid?: boolean;
  onChange: (prompt: string) => void;
  onBlur?: () => void;
}) {
  const options = useUpstreamVariableOptions({ inlineText: true });
  const inputs = useMemo(() => nodeOptionsToEditorInputs(options), [options]);

  return (
    <div className="flex flex-col gap-1.5">
      <NoxusFormLabel label="Prompt" noMargin />
      <MilkdownProvider>
        <ComplexMarkdownEditor
          value={prompt}
          onChange={onChange}
          placeholder="Describe what to check (mention variables inline)…"
          isDisabled={disabled}
          inputs={inputs}
          invalid={invalid}
          onBlur={onBlur}
        />
      </MilkdownProvider>
    </div>
  );
}

export const blankConditionRow = (): ConditionRow => ({
  logic: "OR",
  condition_type: "REGULAR",
  regular_condition: {
    field_type: "TEXT",
    field: "",
    condition: "CONTAINS",
    value: "",
  },
  ai_condition: { prompt: "" },
});

// ── Single row ──────────────────────────────────────────────────────────────

function ConditionCard({
  nodeId,
  fieldKey,
  index,
  row,
  canDelete,
  disabled,
  showLogicConnector,
  blurred,
  onBlurField,
  onPatchRow,
  onPatchRegular,
  onPatchAI,
  onRemove,
}: {
  nodeId: string;
  fieldKey: string;
  index: number;
  row: ConditionRow;
  canDelete: boolean;
  disabled?: boolean;
  showLogicConnector?: boolean;
  blurred: Set<ConditionBlurKey>;
  onBlurField: (key: ConditionBlurKey) => void;
  onPatchRow: (patch: Partial<ConditionRow>) => void;
  onPatchRegular: (
    patch: Partial<NonNullable<ConditionRow["regular_condition"]>>
  ) => void;
  onPatchAI: (
    patch: Partial<NonNullable<ConditionRow["ai_condition"]>>
  ) => void;
  onRemove: () => void;
}) {
  const isAI = row.condition_type === "AI";
  const regular = row.regular_condition ?? {};
  const fieldType = regular.field_type ?? "TEXT";
  const ops = opsFor(fieldType);
  const opTakesValue = !VALUELESS_OPS.has(regular.condition ?? "");

  const fieldBlurKey = `${index}:field` as const;
  const valueBlurKey = `${index}:value` as const;
  const promptBlurKey = `${index}:prompt` as const;

  const showFieldWarning =
    blurred.has(fieldBlurKey) && !isVariableRef(regular.field);
  const showValueWarning =
    fieldType !== "BOOLEAN" &&
    opTakesValue &&
    blurred.has(valueBlurKey) &&
    isEmptyBindableValue(regular.value);
  const showPromptWarning =
    blurred.has(promptBlurKey) &&
    isEmptyBindableValue(row.ai_condition?.prompt);

  return (
    <div className="relative w-full">
      {showLogicConnector ? (
        <div className="absolute -top-7 left-4 z-10 bg-background px-0.5">
          <Select
            disabled={disabled}
            value={row.logic ?? "OR"}
            onValueChange={(v) => onPatchRow({ logic: v as "OR" | "AND" })}
          >
            <SelectTrigger className="h-7 min-w-16 w-fit text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="OR">OR</SelectItem>
              <SelectItem value="AND">AND</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <div className="absolute -right-3 -top-3 z-10 rounded-full bg-background">
        <Button
          type="button"
          variant="outline"
          size="iconxs"
          disabled={disabled || !canDelete}
          className="rounded-full text-muted-foreground"
          onClick={onRemove}
          title="Remove condition"
        >
          <Trash2 className="size-3" strokeWidth={1.25} />
        </Button>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-border bg-background p-4">
        <div className="flex items-center justify-between gap-3">
          <NoxusFormLabel
            noMargin
            label={
              <>
                AI condition
                <span className="mt-1 block text-xs font-normal text-muted-foreground">
                  Give a condition to AI
                </span>
              </>
            }
          />
          <Switch
            disabled={disabled}
            checked={isAI}
            onCheckedChange={(v) =>
              onPatchRow({ condition_type: v ? "AI" : "REGULAR" })
            }
          />
        </div>

        {isAI ? (
          <div className="flex flex-col gap-1.5">
            <AIPromptField
              prompt={row.ai_condition?.prompt ?? ""}
              disabled={disabled}
              invalid={showPromptWarning}
              onChange={(prompt) => onPatchAI({ prompt })}
              onBlur={() => onBlurField(promptBlurKey)}
            />
            <FieldWarning
              show={showPromptWarning}
              message="Prompt cannot be empty."
            />
          </div>
        ) : (
          <>
            {/* Condition type — full-width select, like V1. */}
            <div className="flex flex-col gap-1.5">
              <NoxusFormLabel label="Condition type" noMargin />
              <Select
                disabled={disabled}
                value={fieldType}
                onValueChange={(v) =>
                  onPatchRegular({
                    field_type: v as "TEXT" | "NUMBER" | "BOOLEAN",
                    condition: opsFor(v)[0]?.value ?? "EQUALS",
                  })
                }
              >
                <SelectTrigger className="h-10 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Field row — 2-col grid (V1 uses two equal halves: the field
              picker on the left, the operator on the right). */}
            <div className="flex flex-col gap-1.5">
              <NoxusFormLabel label="Field" noMargin />
              <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col gap-1.5">
                  <div
                    className={cn(
                      showFieldWarning &&
                        "rounded-md ring-1 ring-inset ring-warning-400/70"
                    )}
                  >
                    <BindableInput
                      nodeId={nodeId}
                      value={regular.field ?? ""}
                      disabled={disabled}
                      dataType={dataTypeFor(fieldType)}
                      placeholder="Select variable…"
                      variableOnly
                      onChange={(next) => onPatchRegular({ field: next })}
                      onBlur={() => onBlurField(fieldBlurKey)}
                    />
                  </div>
                  <FieldWarning
                    show={showFieldWarning}
                    message="Select a variable."
                  />
                </div>
                <Select
                  disabled={disabled}
                  value={regular.condition ?? ops[0]?.value ?? ""}
                  onValueChange={(v) => onPatchRegular({ condition: v })}
                >
                  <SelectTrigger className="h-10 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ops.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {opTakesValue ? (
              <div className="flex flex-col gap-1.5">
                <NoxusFormLabel label="Value" noMargin />
                <div
                  className={cn(
                    showValueWarning &&
                      "rounded-md ring-1 ring-inset ring-warning-400/70"
                  )}
                >
                  <BindableInput
                    nodeId={nodeId}
                    value={regular.value ?? ""}
                    disabled={disabled}
                    dataType={dataTypeFor(fieldType)}
                    placeholder="Value"
                    onChange={(next) => onPatchRegular({ value: next })}
                    onBlur={() => onBlurField(valueBlurKey)}
                  />
                </div>
                <FieldWarning
                  show={showValueWarning}
                  message="Value cannot be empty."
                />
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

// ── Top-level field ─────────────────────────────────────────────────────────

export function BindableConditionField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  disabled,
  error,
  isRequired,
  showOptionalLabel,
  value: controlledValue,
  onChange: controlledOnChange,
  hideLabel,
}: {
  nodeId: string;
  fieldKey: string;
  label: string;
  tooltip?: string;
  description?: string;
  disabled?: boolean;
  error?: string;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  value?: ConditionRow[];
  onChange?: (rows: ConditionRow[]) => void;
  hideLabel?: boolean;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const watched = form.watch(path);
  const raw = controlledValue !== undefined ? controlledValue : watched;
  const rows: ConditionRow[] = Array.isArray(raw) ? raw : [];
  const [blurred, setBlurred] = useState<Set<ConditionBlurKey>>(
    () => new Set()
  );

  const markBlurred = useCallback((key: ConditionBlurKey) => {
    setBlurred((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const readRows = useCallback((): ConditionRow[] => {
    if (controlledValue !== undefined) {
      return Array.isArray(controlledValue) ? controlledValue : [];
    }
    const current = form.getValues(path);
    return Array.isArray(current) ? current : [];
  }, [controlledValue, form, path]);

  const write = useCallback(
    (next: ConditionRow[]) => {
      if (controlledOnChange) {
        controlledOnChange(next);
        return;
      }
      form.setValue(path, next, { shouldDirty: true, shouldTouch: true });
    },
    [controlledOnChange, form, path]
  );

  const patchRow = (i: number, patch: Partial<ConditionRow>) => {
    const current = readRows();
    const next = current.slice();
    next[i] = { ...(next[i] ?? blankConditionRow()), ...patch };
    write(next);
  };
  const patchRegular = (
    i: number,
    patch: Partial<NonNullable<ConditionRow["regular_condition"]>>
  ) => {
    const current = readRows();
    const row = current[i] ?? blankConditionRow();
    const next = current.slice();
    next[i] = {
      ...row,
      regular_condition: { ...(row.regular_condition ?? {}), ...patch },
    };
    write(next);
  };
  const patchAI = (
    i: number,
    patch: Partial<NonNullable<ConditionRow["ai_condition"]>>
  ) => {
    const current = readRows();
    const row = current[i] ?? blankConditionRow();
    const next = current.slice();
    next[i] = {
      ...row,
      ai_condition: { ...(row.ai_condition ?? {}), ...patch },
    };
    write(next);
  };
  const addRow = () => write([...readRows(), blankConditionRow()]);
  const removeRow = (i: number) =>
    write(readRows().filter((_, idx) => idx !== i));

  return (
    <div className="flex flex-col gap-1">
      {!hideLabel ? (
        <NoxusFormLabel
          label={label}
          tooltip={tooltip}
          name={fieldKey}
          noMargin
          isDisabled={disabled}
          isRequired={isRequired}
          showOptionalLabel={showOptionalLabel}
        />
      ) : null}
      <PinFieldContext.Provider value={{}}>
        <div
          className={cn(
            "flex flex-col gap-6 pr-3 pt-3",
            error && "rounded-md ring-1 ring-inset ring-warning-400/70 p-1.5"
          )}
        >
          {rows.map((row, i) => (
            <ConditionCard
              key={i}
              nodeId={nodeId}
              fieldKey={fieldKey}
              index={i}
              row={row}
              canDelete={rows.length > 1}
              disabled={disabled}
              showLogicConnector={i > 0}
              blurred={blurred}
              onBlurField={markBlurred}
              onPatchRow={(p) => patchRow(i, p)}
              onPatchRegular={(p) => patchRegular(i, p)}
              onPatchAI={(p) => patchAI(i, p)}
              onRemove={() => removeRow(i)}
            />
          ))}

          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn("w-full", rows.length > 0 && "-mt-2")}
            onClick={addRow}
          >
            <Plus className="size-4 mr-1" /> Add more
          </Button>
        </div>
      </PinFieldContext.Provider>
      {description && !error ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      {error ? (
        <NoxusAlert look="banner" title={error} variant="warning" size="xs" />
      ) : null}
    </div>
  );
}
